import { useCallback, useEffect, useState } from 'react'

import { Button } from '@circlefin/components'
import { useController } from '@circlefin/form'
import Trans from 'next-translate/Trans'
import useTranslation from 'next-translate/useTranslation'

import { MultipleFileUploader } from '../MultipleFileUploader/MultipleFileUploader'

import { CountryOfId } from './CountryOfId/CountryOfId'
import { FormOfId } from './FormOfId/FormOfId'

import type { ComboboxItem } from '@circlefin/components/lib/Combobox'
import type { DropdownItem } from '@circlefin/components/lib/Dropdown'
import type {
  CountryCode,
  DocumentType,
  L3OnboardingFlowGroup,
  OnboardingDocument,
} from '@shared/graphql'

export interface GovernmentIssuedIdProps {
  /**
   * Group name.
   */
  groupName: L3OnboardingFlowGroup
  /**
   * Disabled?
   */
  disabled?: boolean
  /**
   * Loading?
   */
  loading?: boolean
  /**
   * Warning?
   */
  warning?: boolean
  /**
   * Section title.
   */
  title?: string
  /**
   * Section subtitle.
   */
  subtitle?: React.ReactNode
  /**
   * Field name.
   */
  name: string
}

export const GovernmentIssuedId: React.FC<GovernmentIssuedIdProps> = ({
  name = 'governmentIssuedId',
  groupName,
  warning = false,
  disabled = false,
  loading = false,
  title,
  subtitle,
}) => {
  const { t } = useTranslation('l3Onboarding')
  const [showAddAnotherId, setShowAddAnotherId] = useState(false)
  const [formOfId, setFormOfId] = useState<DocumentType>()
  const [countryOfId, setCountryOfId] = useState<CountryCode>()

  const { field } = useController({ name })

  const filesValue = (field.value ?? []) as OnboardingDocument[]

  useEffect(() => {
    // Hide add another id button in warning state
    setShowAddAnotherId(!warning && filesValue.length > 0)
  }, [filesValue.length, warning])

  const onAfterUpload = useCallback(() => {
    setFormOfId(undefined)
    setCountryOfId(undefined)
  }, [])

  const handleAddAnotherId = useCallback(() => {
    setShowAddAnotherId(false)
  }, [])

  const handleUpdateFormOfId = useCallback(
    (change: DropdownItem<DocumentType>) => {
      setFormOfId(change.value)
    },
    [],
  )

  const handleUpdateCountryOfId = useCallback(
    (change: ComboboxItem<CountryCode> | null) => {
      setCountryOfId(change?.value)
    },
    [],
  )

  return (
    <fieldset
      className="flex min-w-0 flex-col gap-y-4 rounded-sm focus:focus-ring"
      data-field-group="true"
      tabIndex={-1}
    >
      <legend className="text-neutral-strong font-circular-bold">
        {title ?? t`forms.common.formOfId.governmentId.title`}
      </legend>
      <span className="text-neutral-subtle type-body-sm">
        {subtitle ?? (
          <Trans
            components={{ br: <br /> }}
            i18nKey="l3Onboarding:forms.common.formOfId.governmentId.subtitle"
          />
        )}
      </span>

      {showAddAnotherId && (
        <Button
          disabled={disabled || loading}
          onClick={handleAddAnotherId}
          variant="secondary"
        >
          {t`forms.common.formOfId.governmentId.addAnother`}
        </Button>
      )}

      {!showAddAnotherId && (
        <>
          <FormOfId
            disabled={disabled}
            loading={loading}
            onChange={handleUpdateFormOfId}
            value={formOfId}
            warning={warning}
          />

          <CountryOfId
            className="w-full"
            disabled={disabled}
            loading={loading}
            onChange={handleUpdateCountryOfId}
            value={countryOfId}
            warning={warning}
          />
        </>
      )}

      <MultipleFileUploader
        disabled={loading || disabled}
        documentType={formOfId}
        documents={filesValue}
        fileTypes={['image/jpeg', 'image/png', 'application/pdf']}
        groupName={groupName}
        issuedCountry={countryOfId}
        name={name}
        onAfterUpload={onAfterUpload}
        showUpload={
          !showAddAnotherId && Boolean(formOfId) && Boolean(countryOfId)
        }
        warning={warning}
      />
    </fieldset>
  )
}
